// eRecords theme for every GoAlert page: the palette from brand.ts plus
// component styling after concierge-ui (flat surfaces with hairline borders,
// rounded corners, a system sans font, no uppercase buttons).
import React from 'react'
import GlobalStyles from '@mui/material/GlobalStyles'
import { ThemeOptions } from '@mui/material/styles'
import { brandColors, brandPalette, sans } from './brand'

type Mode = 'light' | 'dark'

// calendarStyles restyles react-big-calendar (schedule and user shift
// calendars): hairline grid, quiet headers, soft shift pills, a dark "today"
// badge. !important beats the inline day colors upstream sets.
function calendarStyles(
  mode: Mode,
): Record<string, Record<string, string | number>> {
  const c = brandColors[mode]
  const shift = mode === 'dark' ? '#3a3536' : '#ece5dc'
  const override = mode === 'dark' ? '#2f3a36' : '#e1ece5'
  return {
    '.rbc-calendar': { fontFamily: sans, color: c.ink },
    '.rbc-month-view, .rbc-time-view': {
      border: `1px solid ${c.line} !important`,
      borderRadius: '14px',
      overflow: 'hidden',
      backgroundColor: c.surface,
    },
    '.rbc-header': {
      border: 'none !important',
      borderBottom: `1px solid ${c.line} !important`,
      padding: '10px 0 !important',
      fontSize: '11px',
      fontWeight: 600,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: c.muted,
    },
    '.rbc-month-row + .rbc-month-row': {
      borderTop: `1px solid ${c.line} !important`,
    },
    '.rbc-day-bg': {
      border: 'none !important',
      backgroundColor: 'transparent !important',
    },
    '.rbc-day-bg + .rbc-day-bg': {
      borderLeft: `1px solid ${c.line} !important`,
    },
    '.rbc-off-range-bg': { backgroundColor: 'transparent !important' },
    '.rbc-today': { backgroundColor: 'transparent !important' },
    '.rbc-date-cell': { padding: '6px 8px', fontSize: '12px', color: c.muted },
    '.rbc-date-cell.rbc-off-range': { opacity: 0.55 },
    '.rbc-date-cell.rbc-now a': {
      display: 'inline-block',
      minWidth: '22px',
      textAlign: 'center',
      borderRadius: '999px',
      padding: '1px 6px',
      backgroundColor: c.ink,
      color: c.surface,
      fontWeight: 600,
    },
    '.rbc-event, .rbc-day-slot .rbc-background-event': {
      border: 'none !important',
      borderRadius: '8px !important',
      padding: '3px 8px !important',
      fontSize: '12px',
      fontWeight: 500,
      boxShadow: 'none !important',
    },
    '.rbc-event.erecords-shift': {
      backgroundColor: `var(--p-bg, ${shift}) !important`,
      color: `${c.ink} !important`,
      borderLeft: `3px solid var(--p-line, ${c.muted}) !important`,
    },
    '.rbc-event.erecords-override': {
      backgroundColor: `var(--p-bg, ${override}) !important`,
      color: `${c.ink} !important`,
      border: `1px dashed var(--p-line, ${c.ok}) !important`,
      borderLeft: `3px solid var(--p-line, ${c.ok}) !important`,
    },
    '.rbc-event.rbc-selected, .rbc-event:focus': {
      outline: `2px solid ${c.muted} !important`,
      outlineOffset: '1px',
    },
    '.rbc-row-segment': { padding: '1px 4px' },
    '.rbc-show-more': {
      color: c.muted,
      fontSize: '12px',
      backgroundColor: 'transparent',
    },
    '.rbc-time-header-content, .rbc-time-content, .rbc-timeslot-group, .rbc-time-slot, .rbc-day-slot .rbc-time-slot':
      { borderColor: `${c.line} !important` },
    '.rbc-time-gutter .rbc-label': { fontSize: '11px', color: c.muted },
    '.rbc-current-time-indicator': { backgroundColor: `${c.brand} !important` },
  }
}

// withBrand merges the eRecords look into upstream's theme options.
export function withBrand(options: ThemeOptions, mode: Mode): ThemeOptions {
  const c = brandColors[mode]
  return {
    ...options,
    palette: brandPalette(mode),
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: sans,
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      ...options.components,
      MuiIconButton: { defaultProps: { color: 'default' } },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'inherit' },
        styleOverrides: {
          root: {
            backgroundColor: c.surface,
            backgroundImage: 'none',
            color: c.ink,
            borderBottom: `1px solid ${c.line}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: c.surface,
            backgroundImage: 'none',
            borderRight: `1px solid ${c.line}`,
          },
        },
      },
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: 'none' } },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: 16,
            border: `1px solid ${c.line}`,
            backgroundColor: c.surface,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 18, border: `1px solid ${c.line}` },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            border: `1px solid ${c.line}`,
            boxShadow:
              '0 1px 2px rgba(51,49,50,0.05), 0 8px 28px rgba(51,49,50,0.10)',
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: { root: { borderRadius: 10 } },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            boxShadow:
              '0 1px 2px rgba(51,49,50,0.10), 0 8px 24px rgba(51,49,50,0.18)',
          },
        },
      },
      MuiChip: {
        styleOverrides: { root: { fontWeight: 500 } },
      },
      MuiListItemButton: {
        styleOverrides: { root: { borderRadius: 10 } },
      },
      MuiOutlinedInput: {
        styleOverrides: { root: { borderRadius: 10 } },
      },
      MuiDivider: {
        styleOverrides: { root: { borderColor: c.line } },
      },
      MuiTableCell: {
        styleOverrides: { root: { borderColor: c.line } },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: c.ink, color: c.surface, fontSize: 12 },
        },
      },
    },
  }
}

// brandIf applies the eRecords look. It used to depend on the theme color;
// now it always applies (the color picker is gone), so a color saved by an
// older version in localStorage cannot switch back to GoAlert's look.
export function brandIf(
  sourceColor: string,
  mode: Mode,
  options: ThemeOptions,
): ThemeOptions {
  return withBrand(options, mode)
}

// BrandGlobalStyles fixes text outside MUI components (GoAlert renders no
// CssBaseline, so plain text fell back to the browser's serif font).
export function BrandGlobalStyles({ mode }: { mode: Mode }): React.JSX.Element {
  const c = brandColors[mode]
  return (
    <GlobalStyles
      styles={{
        html: { colorScheme: mode },
        body: { fontFamily: sans, backgroundColor: c.bg, color: c.ink },
        a: { color: c.ink },
        '::selection': { background: c.soft },
        ...calendarStyles(mode),
      }}
    />
  )
}
